import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn } from "@/components/kit";
import { useBranding } from "@/context/BrandingContext";
import { Palette, Upload, Save, Building2, Phone, Mail, Globe, MapPin, BadgeCheck, Loader2, Map } from "lucide-react";
import { toast } from "sonner";
import BrandingContent from "@/components/BrandingContent";
import { UpgradeCatalogEditor } from "@/components/UpgradeCatalogEditor";

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
const field = "w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy";

export default function Branding() {
  const { branding, refresh } = useBranding();
  const [f, setF] = useState(branding);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/branding").then((r) => { setF(r.data); setLoaded(true); }).catch(() => {}); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo must be under 3 MB");
    set("logo_url", await readFile(file));
    toast.success("Logo loaded — click Save to apply");
  };
  const save = async () => {
    setSaving(true);
    try { await api.put("/branding", f); await refresh(); toast.success("Branding saved — it's live across the app"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const Text = ({ k, label, icon: Icon, ph }) => (
    <div>
      <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-1.5"><Icon className="w-3.5 h-3.5" /> {label}</label>
      <input data-testid={`brand-${k}`} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className={field} />
    </div>
  );

  return (
    <div>
      <PageHeader title="Company Branding" subtitle="Your logo and contact info appear on proposals, invoices, the instant-quote page and email."
        actions={<Btn variant="red" onClick={save} disabled={saving || !loaded} testid="save-branding">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Btn>} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="mb-card p-5 flex flex-col items-center text-center" data-testid="brand-logo-panel">
          <div className="text-xs font-semibold uppercase text-slate-500 mb-3 self-start flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> Logo</div>
          <div className="w-40 h-40 rounded-2xl bg-white border-2 border-slate-200 flex items-center justify-center overflow-hidden mb-3">
            {f.logo_url ? <img src={f.logo_url} alt="logo" className="w-full h-full object-contain" /> : <span className="text-slate-300 text-xs">No logo</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickLogo} className="hidden" data-testid="brand-logo-file" />
          <Btn onClick={() => fileRef.current?.click()} testid="brand-logo-upload"><Upload className="w-4 h-4" /> Upload Logo</Btn>
          <div className="w-full mt-3">
            <label className="text-[11px] font-semibold uppercase text-slate-400">…or paste an image URL</label>
            <input data-testid="brand-logo_url" value={f.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…" className={`${field} mt-1 text-xs`} />
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="mb-card p-5 grid sm:grid-cols-2 gap-4">
            <Text k="company_name" label="Company Name" icon={Building2} ph="Masterbuilt Roofing" />
            <Text k="tagline" label="Tagline" icon={BadgeCheck} ph="Built For You" />
            <Text k="phone" label="Phone" icon={Phone} ph="(555) 123-4567" />
            <Text k="email" label="Email" icon={Mail} ph="hello@company.com" />
            <Text k="website" label="Website" icon={Globe} ph="www.company.com" />
            <Text k="license" label="License #" icon={BadgeCheck} ph="TX-RCL-000000" />
            <div className="sm:col-span-2"><Text k="address" label="Business Address" icon={MapPin} ph="123 Main St, City, ST" /></div>
          </div>
          <div className="mb-card p-5 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500 mb-1.5 block">Primary Color</label>
              <div className="flex items-center gap-2"><input type="color" data-testid="brand-primary_color" value={f.primary_color || "#1e3a5f"} onChange={(e) => set("primary_color", e.target.value)} className="w-12 h-10 rounded-lg border-2 border-slate-200 cursor-pointer" /><input value={f.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className={`${field} font-num`} /></div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500 mb-1.5 block">Accent Color</label>
              <div className="flex items-center gap-2"><input type="color" data-testid="brand-accent_color" value={f.accent_color || "#C8102E"} onChange={(e) => set("accent_color", e.target.value)} className="w-12 h-10 rounded-lg border-2 border-slate-200 cursor-pointer" /><input value={f.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} className={`${field} font-num`} /></div>
            </div>
          </div>
          <div className="mb-card p-5">
            <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-1.5"><Map className="w-3.5 h-3.5" /> Google Maps (browser) API Key (optional)</label>
            <input data-testid="brand-google_aerial_key" value={f.google_aerial_key || ""} onChange={(e) => set("google_aerial_key", e.target.value)} placeholder="Leave blank to use free satellite imagery" className={field} />
            <p className="text-[11px] text-slate-400 mt-2">Used only for Static Maps + Street View imagery on the Instant Quote preview. Restrict it to those 2 APIs + your domain. Leave blank to use free Esri satellite.</p>
            <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-1.5 mt-4"><Map className="w-3.5 h-3.5" /> Google Server Key — Solar / Geocoding / Places (optional)</label>
            <input data-testid="brand-google_server_key" value={f.google_server_key || ""} onChange={(e) => set("google_server_key", e.target.value)} placeholder="Paste a new key to update (shows last 4 once saved)" className={field} />
            <p className="text-[11px] text-slate-400 mt-2">Kept server-side and never sent to the browser. Enable Solar API, Geocoding API &amp; Places API (New) on this key. When set, roof size is measured from satellite (Google Solar); when blank, we fall back to an approximate estimate.</p>
            <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1 mb-1.5 mt-4">Solar measurement adjustment %</label>
            <input data-testid="brand-solar_adjust_pct" type="number" value={f.solar_adjust_pct ?? 5} onChange={(e) => set("solar_adjust_pct", e.target.value === "" ? 0 : Number(e.target.value))} placeholder="5" className={`${field} font-num`} />
            <p className="text-[11px] text-slate-400 mt-2">Added to the Google Solar area (before the standard 15% waste factor) to account for eave overhangs the Solar API tends to undercount. Default +5%.</p>
          </div>
        </div>
      </div>

      {loaded && <UpgradeCatalogEditor f={f} set={set} />}
      <BrandingContent f={f} set={set} />
    </div>
  );
}
